import React, { useEffect, useState } from "react";

/*
 * The spots as GTA San Andreas would show them: the round radar with a blip for every spot (placed by its
 * real coordinates) on one side, a game-menu list on the other, and the middle left open for whatever plays
 * behind the section. The selected spot's blip flashes and its name comes up like a GTA zone name (images
 * made with the Pricedown font: its free licence allows pictures, not the font on a site). Until someone
 * points at one, the selection walks through them on its own.
 */
const CENTER = { lat: 38.3, lon: 23.4 }; // the middle of the radar
const RANGE = 3.5; // degrees of latitude from the middle to the rim
const CYCLE_MS = 2400;

// where a point sits on the radar, 0-100 across (50 is the middle)
const project = (lat, lon) => [
  50 + (((lon - CENTER.lon) * Math.cos((CENTER.lat * Math.PI) / 180)) / RANGE) * 50,
  50 + ((CENTER.lat - lat) / RANGE) * 50,
];
const place = ({ lat, lon }) => {
  const [x, y] = project(lat, lon);
  return { left: `${x}%`, top: `${y}%` };
};

// Greece, simplified (lon, lat), for the land under the blips
const LAND = [
  // the mainland with the Peloponnese, clockwise from the Albanian border on the coast
  [[20.0, 39.65], [20.4, 40.0], [20.75, 40.35], [21.1, 40.7], [21.6, 40.95], [22.3, 41.15], [23.0, 41.3],
    [23.7, 41.4], [24.5, 41.55], [25.3, 41.25], [26.0, 41.35], [26.35, 41.72], [26.1, 41.3], [26.03, 40.75],
    [25.6, 40.85], [25.0, 40.95], [24.4, 40.9], [23.9, 40.75], [23.7, 40.6], [23.95, 40.38], [24.35, 40.15],
    [23.85, 40.28], [23.75, 39.95], [23.45, 40.2], [23.35, 39.95], [23.0, 40.3], [22.9, 40.62], [22.6, 40.45],
    [22.6, 40.0], [22.95, 39.6], [23.25, 39.4], [23.2, 39.15], [22.9, 39.2], [22.75, 38.9], [23.3, 38.6],
    [23.6, 38.4], [24.0, 38.2], [24.05, 37.95], [24.02, 37.65], [23.65, 37.93], [23.4, 38.0], [23.0, 37.93],
    [23.2, 37.62], [23.45, 37.35], [22.95, 37.5], [22.75, 36.85], [23.2, 36.43], [22.6, 36.75], [22.5, 36.38],
    [22.2, 36.9], [21.7, 36.8], [21.6, 37.2], [21.3, 37.65], [21.1, 37.85], [21.6, 38.18], [21.78, 38.32],
    [21.15, 38.3], [20.8, 38.75], [20.75, 39.05], [20.4, 39.3], [20.2, 39.55]],
  // Euboea
  [[22.85, 38.85], [23.3, 39.02], [23.6, 38.78], [24.1, 38.62], [24.6, 38.02], [24.3, 37.98], [23.6, 38.42], [23.2, 38.62]],
  // Crete
  [[23.55, 35.3], [24.0, 35.55], [24.5, 35.4], [25.1, 35.35], [25.7, 35.3], [26.3, 35.28], [26.2, 34.95],
    [25.0, 34.95], [24.0, 35.12], [23.6, 35.2]],
  // Corfu, Kefalonia, Lesvos, Chios, Rhodes
  [[19.65, 39.8], [19.95, 39.8], [20.1, 39.45], [19.85, 39.55]],
  [[20.35, 38.4], [20.65, 38.3], [20.75, 38.1], [20.45, 38.12]],
  [[25.85, 39.3], [26.4, 39.35], [26.6, 39.0], [26.1, 39.05]],
  [[25.85, 38.6], [26.15, 38.55], [26.1, 38.25], [25.9, 38.3]],
  [[27.7, 36.2], [28.25, 36.45], [28.2, 36.05], [27.8, 35.9]],
];
// the Gulf of Corinth, cut back into the land
const GULF = [[21.78, 38.32], [22.3, 38.18], [22.75, 38.02], [23.0, 37.95], [22.85, 38.08], [22.4, 38.38], [21.85, 38.38]];
const points = (shape) => shape.map(([lon, lat]) => project(lat, lon).map((v) => v.toFixed(1)).join(",")).join(" ");

const SpotsRadar = ({ spots, play }) => {
  const [selected, setSelected] = useState(0);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!play || touched) return undefined;
    const timer = setTimeout(() => setSelected((i) => (i + 1) % spots.length), CYCLE_MS);
    return () => clearTimeout(timer);
  }, [play, touched, selected, spots.length]);

  const pick = (i) => {
    setTouched(true);
    setSelected(i);
  };
  const spot = spots[selected];

  return (
    <div className="grid md:grid-cols-[auto_1fr_auto] gap-10 items-center">
      <div className="flex flex-col items-center">
        <div className="gta-radar" role="img" aria-label="Χάρτης με τα καταστήματα">
          <svg className="gta-radar-map" viewBox="0 0 100 100" aria-hidden="true">
            {LAND.map((shape, i) => (
              <polygon key={i} points={points(shape)} />
            ))}
            <polygon className="gta-radar-sea" points={points(GULF)} />
          </svg>
          <span className="gta-radar-north">N</span>
          {spots.map((s, i) => (
            <button
              key={s.city}
              type="button"
              className={`gta-blip ${s.og ? "gta-blip-og" : ""} ${i === selected ? "gta-blip-on" : ""}`}
              style={place(s)}
              onMouseEnter={() => pick(i)}
              onClick={() => pick(i)}
              aria-label={s.city}
            />
          ))}
        </div>
        {/* the zone name, as GTA shows it when you drive into an area */}
        <div key={spot.city} className="gta-zone mt-6 text-center">
          <img className="gta-zone-name" src={`${process.env.PUBLIC_URL}/images/spots/zone-${spot.zone}.webp`} alt={spot.city} />
          <div className="gta-zone-sub">{spot.address}</div>
        </div>
      </div>

      {/* the middle stays open: he walks there, behind the section */}
      <div className="hidden md:block" aria-hidden="true" />

      <ul className="gta-menu">
        {spots.map((s, i) => (
          <li key={s.city}>
            <button
              type="button"
              className={`gta-menu-item ${i === selected ? "gta-menu-on" : ""}`}
              onMouseEnter={() => pick(i)}
              onFocus={() => pick(i)}
              onClick={() => pick(i)}
            >
              <span>{s.city}</span>
              <span className="gta-menu-status">{s.status}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default SpotsRadar;
